import classNames from 'classnames';
import type Quill from 'quill/core';
import type { Bounds } from 'quill/core';
import React, { useEffect, useState } from 'react';
import type {
  DropdownMenuItemType,
  DropdownMenuSection,
  RenderItemFn,
} from '../../components/DropdownMenu';
import DropdownMenu, {
  getItemFromSectionsByIndex,
} from '../../components/DropdownMenu';
import Tether from '../../components/Tether';
import useIsLayoutSize from '../../post/utils/useIsLayoutSize';
import { LayoutSize } from '../../post/utils/useLayoutSize';
import addTestId from '../../utils/addTestId';
import useEventListener from '../../utils/useEventListener';
import PrefixInputBackground from './PrefixInputBackground';
import * as styles from './PrefixTriggeredMenu.module.scss';

const OVERRIDE_SHORTCUTS = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Enter',
  'Escape',
];

interface PrefixTriggeredMenuProps<T> {
  quill: Quill;
  onSelect: (item: DropdownMenuItemType<T>) => void;
  sections: DropdownMenuSection<T>[];
  query: string;
  onClose: () => void;
  clientRects: [Bounds, ...Bounds[]];
  renderItem: RenderItemFn<T>;
  containerSize?: 'small' | 'regular';
  testId?: string;
  withMenuPadding?: boolean;
}

const PrefixTriggeredMenu = <T,>({
  quill,
  onSelect,
  sections,
  query,
  clientRects,
  onClose,
  renderItem,
  containerSize = 'regular',
  testId,
  withMenuPadding,
}: PrefixTriggeredMenuProps<T>) => {
  const isEmpty = !sections.some(
    (section) => section.items && section.items.length > 0,
  );
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [query]);

  useEffect(() => {
    if (/^\s/.test(query)) {
      onClose();
    }
  }, [query, onClose]);

  const handleSelect = (item: DropdownMenuItemType<T>) => {
    onSelect(item);
    onClose();
  };

  useEffect(() => {
    if (isEmpty) return undefined;

    const binding = { key: OVERRIDE_SHORTCUTS, handler: () => false };
    // @ts-expect-error
    quill.keyboard.prependBinding(binding);
    return () =>
      // @ts-expect-error
      quill.keyboard.removeBinding(binding);
  }, [quill, isEmpty]);

  useEffect(() => {
    if (isEmpty && query.includes(' ')) {
      onClose();
    }
  }, [isEmpty, query, onClose]);

  useEventListener(
    quill.root,
    'keydown',
    (event) => {
      if (event.key === 'ArrowRight' || event.key === 'Tab') {
        const item = getItemFromSectionsByIndex(sections, index);
        if (item) {
          handleSelect(item);
        }
      }
    },
    { skip: isEmpty },
  );

  // We want the last clientRect because it's the one that's closest to the cursor
  const bounds = clientRects.at(-1) ?? clientRects[0];
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  return isEmpty ? null : (
    <>
      <div
        ref={setAnchorElement}
        className={styles.reference}
        style={{
          left: bounds.left,
          top: bounds.top,
          height: bounds.height,
        }}
      />
      <Tether
        anchorElement={anchorElement}
        placement="bottom-start"
        offset={8}
        positionedView={
          <div
            data-no-print
            className={classNames(styles.container, {
              [styles.small]: containerSize === 'small',
            })}
            {...addTestId(testId)}
          >
            <DropdownMenu.Root
              sections={sections}
              index={index}
              withPadding={withMenuPadding}
              onIndexChange={setIndex}
              onSelect={handleSelect}
              onClose={onClose}
              inputContainer={quill.root}
              maxHeight={isMobile ? 200 : 356}
              renderItem={renderItem}
            />
          </div>
        }
      />
      <PrefixInputBackground clientRects={clientRects} />
    </>
  );
};

export default PrefixTriggeredMenu;
